/**
 * Parcels held at gate — photo-placeholder cards, status, "Mark as picked up".
 * Wired: GET /api/services/parcels + optimistic POST /api/services/parcels/<id>/picked
 * with revert. Port of screens-services.jsx Parcels.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { fmtWhen, str, useGateList } from '@/components/gate/api';
import { InfoNote } from '@/components/gate/bits';
import { Icon, type IconName } from '@/components/gate/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Card, EmptyState, Pill, Screen, SectionLabel, Skeleton, TopBar } from '@/ui';

type ParcelRow = { id: string; from: string; desc: string; at: string; picked: boolean };

/** Arch-shaped parcel icon frame — the design's box-at-the-gate motif. */
function ArchIconTile({ name, muted }: { name: IconName; muted?: boolean }) {
  const { colors } = useTheme();
  return (
    <View style={{
      width: 44, height: 50, borderTopLeftRadius: 22, borderTopRightRadius: 22,
      borderBottomLeftRadius: 6, borderBottomRightRadius: 6,
      backgroundColor: muted ? colors.canvas2 : colors.primary100,
      alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon name={name} size={20} color={muted ? colors.borderFirm : colors.primary500} />
    </View>
  );
}

function toRow(p: unknown, i: number): ParcelRow {
  return {
    id: str(p, 'id', 'code') ?? `PR-${i}`,
    from: str(p, 'from', 'courier', 'sender') ?? 'Courier',
    desc: str(p, 'desc', 'description') ?? 'Parcel',
    at: fmtWhen(str(p, 'heldAt', 'createdAt', 'arrivedAt'), str(p, 'at', 'when')),
    picked: (str(p, 'status') ?? '').toLowerCase() === 'picked',
  };
}

export default function ParcelsScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { items, setItems, state } = useGateList<unknown>('/api/services/parcels', 'parcels');
  const [error, setError] = React.useState<string | null>(null);
  const rows = items.map(toRow);

  const markPicked = async (id: string) => {
    setError(null);
    const prev = items;
    // optimistic: flip status locally
    setItems(items.map((p) => (str(p, 'id', 'code') === id ? { ...(p as object), status: 'picked' } : p)));
    try {
      await api.post<unknown>(`/api/services/parcels/${encodeURIComponent(id)}/picked`);
    } catch {
      setItems(prev); // revert
      setError(t('parcels_pick_error'));
    }
  };

  const pending = rows.filter((p) => !p.picked);
  const done = rows.filter((p) => p.picked);

  return (
    <Screen scroll={false}>
      <TopBar title={t('parcels_title')} onBack={() => router.back()} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {/* teaser to the delivery auto-approval PIN — kept as its own screen
            (existing route), this just surfaces it the way the design's
            merged "Parcels" frame does. */}
        <Card
          onPress={() => router.push('/delivery-pin')}
          style={{ borderColor: colors.accent500, backgroundColor: colors.accent100, marginBottom: space.s4 }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
            <View style={{ flex: 1 }}>
              <AppText variant="label" style={{ color: colors.accent700 }}>{t('parcels_pin_title')}</AppText>
              <AppText variant="bodySm" tone="secondary" style={{ marginTop: 2 }}>
                {t('parcels_pin_sub')}
              </AppText>
            </View>
            <Icon name="chevron-right" size={18} color={colors.accent700} />
          </View>
        </Card>

        {error ? <InfoNote icon="info" tint={colors.danger500} style={{ marginBottom: space.s3 }}>{error}</InfoNote> : null}
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>
            {[0, 1, 2].map((i) => <Skeleton key={i} h={88} />)}
          </View>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon name="package" size={40} color={colors.textTertiary} />}
            title={state === 'error' ? t('parcels_empty_err_title') : t('parcels_empty_title')}
            body={state === 'error' ? t('parcels_empty_err_body') : t('parcels_empty_body')}
          />
        ) : (
          <>
            {pending.length > 0 ? (
              <>
                <SectionLabel>{t('parcels_at_desk')} · {pending.length}</SectionLabel>
                <View style={{ gap: space.s3 }}>
                  {pending.map((p) => (
                    <Card key={p.id}>
                      <View style={{ flexDirection: 'row', gap: space.s3, alignItems: 'center' }}>
                        <ArchIconTile name="package" />
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <AppText variant="headingSm" weight="500">{p.from}</AppText>
                          <AppText variant="bodySm" tone="secondary" style={{ marginTop: 2 }}>{p.desc}</AppText>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 2 }}>
                            {p.at ? <AppText variant="bodySm" tone="tertiary">{p.at} · </AppText> : null}
                            <AppText variant="bodySm" tone="tertiary" mono>#{p.id}</AppText>
                          </View>
                        </View>
                        <Button
                          title={t('parcels_mark_picked')}
                          kind="secondary"
                          style={{ paddingVertical: space.s2, paddingHorizontal: space.s3, minHeight: 44 }}
                          onPress={() => markPicked(p.id)}
                        />
                      </View>
                    </Card>
                  ))}
                </View>
              </>
            ) : null}

            {done.length > 0 ? (
              <>
                <SectionLabel style={{ marginTop: pending.length ? space.s6 : 0 }}>{t('parcels_collected')}</SectionLabel>
                <View style={{ gap: space.s3 }}>
                  {done.map((p) => (
                    <Card key={p.id} style={{ opacity: 0.75 }}>
                      <View style={{ flexDirection: 'row', gap: space.s3, alignItems: 'center' }}>
                        <ArchIconTile name="package" muted />
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <AppText variant="bodyMd" weight="600">{p.from}</AppText>
                          <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{p.desc}</AppText>
                        </View>
                        <Pill status="resolved" label={t('pill_picked_up')} />
                      </View>
                    </Card>
                  ))}
                </View>
              </>
            ) : null}
          </>
        )}
      </Screen>
    </Screen>
  );
}
